import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactAPI, contactListAPI, tenantAPI, Contact, ContactList } from '../services/api';
import Icon from '../components/Icon';
import Empty from '../components/ui/Empty';
import { fmtDate } from '../utils/format';
import { useT } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';

/** Initials from a name/email for the mini avatar. */
const initials = (name?: string | null, email?: string | null): string => {
  const base = (name || email || '?').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

/** Deterministic token color for an avatar based on id. */
const AVATAR_COLORS = ['var(--brand-600)', 'var(--ch-sms)', 'var(--ch-viber)', 'var(--accent)'];
const avatarColor = (id: number): string => AVATAR_COLORS[id % AVATAR_COLORS.length];

const Contacts: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { role } = useAuth();

  // Whether this user may open the per-contact messages overview. super_admin
  // always; other roles only when the admin enabled it for their tenant.
  const [canSeeMessages, setCanSeeMessages] = useState(role === 'super_admin');
  useEffect(() => {
    if (role === 'super_admin') { setCanSeeMessages(true); return; }
    tenantAPI.getMySender()
      .then((res) => setCanSeeMessages(!!res.data?.data?.messagesOverview))
      .catch(() => setCanSeeMessages(false));
  }, [role]);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [showUnsubscribedOnly, setShowUnsubscribedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [menu, setMenu] = useState<number | null>(null);

  // Contact overview popup
  const [overviewId, setOverviewId] = useState<number | null>(null);
  const [overview, setOverview] = useState<Contact | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewBusy, setOverviewBusy] = useState(false);
  const [addToListId, setAddToListId] = useState<string>('');

  // Add contact form state
  const [newContact, setNewContact] = useState({ email: '', name: '', phoneNumber: '' });
  // Which lists to add the new contact to
  const [availableLists, setAvailableLists] = useState<ContactList[]>([]);
  const [selectedLists, setSelectedLists] = useState<number[]>([]);

  const menuRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, showUnsubscribedOnly]);

  useEffect(() => {
    // Load lists once for the "add to list" selector
    contactListAPI.getAll({ limit: 1000 })
      .then((res) => { if (res.data.success) setAvailableLists(res.data.data.lists); })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    const onDoc = () => setMenu(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  // Load the selected contact (with its lists) for the overview popup.
  useEffect(() => {
    if (overviewId == null) { setOverview(null); setAddToListId(''); return; }
    let cancelled = false;
    setOverviewLoading(true);
    contactAPI.getOne(overviewId)
      .then((res) => { if (!cancelled && res.data.success) setOverview(res.data.data.contact); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [overviewId]);

  const reloadOverview = async () => {
    if (overviewId == null) return;
    const res = await contactAPI.getOne(overviewId);
    if (res.data.success) setOverview(res.data.data.contact);
  };

  const addContactToList = async (listId: number) => {
    if (!overview) return;
    setOverviewBusy(true);
    try {
      await contactListAPI.addContact(listId, overview.id);
      setAddToListId('');
      await reloadOverview();
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setOverviewBusy(false);
    }
  };

  const removeContactFromList = async (listId: number) => {
    if (!overview) return;
    setOverviewBusy(true);
    try {
      await contactListAPI.removeContact(listId, overview.id);
      await reloadOverview();
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setOverviewBusy(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getAll({
        page,
        limit: 20,
        search,
        unsubscribed: showUnsubscribedOnly ? true : undefined,
      });

      if (response.data.success) {
        setContacts(response.data.data.contacts);
        setTotalPages(response.data.data.pages);
        setSelected([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      setError(t('Email je obavezan', 'Email is required'));
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await contactAPI.create(newContact);

      if (response.data.success) {
        const contactId = response.data.data.contact.id;
        // Add the new contact to any selected lists
        let listMsg = '';
        if (selectedLists.length > 0) {
          await Promise.all(selectedLists.map((listId) => contactListAPI.addContact(listId, contactId)));
          listMsg = t(
            ` Dodat u ${selectedLists.length} list${selectedLists.length > 1 ? 'e' : 'u'}.`,
            ` Added to ${selectedLists.length} list${selectedLists.length > 1 ? 's' : ''}.`,
          );
        }
        setSuccess(t('Kontakt uspešno dodat!', 'Contact added successfully!') + listMsg);
        setShowAddModal(false);
        setNewContact({ email: '', name: '', phoneNumber: '' });
        setSelectedLists([]);
        loadContacts();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!window.confirm(t('Da li ste sigurni da želite da obrišete ovaj kontakt?', 'Are you sure you want to delete this contact?'))) {
      return;
    }

    try {
      await contactAPI.delete(id);
      setSuccess(t('Kontakt uspešno obrisan!', 'Contact deleted successfully!'));
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleToggleUnsubscribe = async (contact: Contact) => {
    const action = contact.unsubscribed
      ? t('aktivirati', 'reactivate')
      : t('označiti kao unsubscribed', 'mark as unsubscribed');
    if (!window.confirm(t(
      `Da li ste sigurni da želite da ${action} kontakt ${contact.email}?`,
      `Are you sure you want to ${action} contact ${contact.email}?`,
    ))) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await contactAPI.update(contact.id, {
        email: contact.email,
        name: contact.name,
        unsubscribed: !contact.unsubscribed,
      });
      setSuccess(contact.unsubscribed
        ? t('Kontakt uspešno aktiviran!', 'Contact reactivated successfully!')
        : t('Kontakt uspešno označen kao unsubscribed!', 'Contact marked as unsubscribed successfully!'));
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleToggleBlacklist = async (contact: Contact) => {
    try {
      setError('');
      setSuccess('');
      await contactAPI.update(contact.id, { blacklisted: !contact.blacklisted });
      setSuccess(contact.blacklisted
        ? t('Kontakt uklonjen sa crne liste.', 'Contact removed from blacklist.')
        : t('Kontakt dodat na crnu listu — neće primati SMS/Viber.', 'Contact blacklisted — will not receive SMS/Viber.'));
      loadContacts();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const allSelected = contacts.length > 0 && selected.length === contacts.length;
  const toggleAll = () => setSelected(allSelected ? [] : contacts.map((c) => c.id));
  const toggleOne = (id: number) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const tagsOf = (c: Contact): string[] => {
    if (!c.customFields) return [];
    return Object.values(c.customFields)
      .filter((v) => v !== null && v !== undefined && v !== '')
      .map((v) => String(v));
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="page page-wide">
        <div className="card card-pad muted">{t('Učitavanje kontakata…', 'Loading contacts…')}</div>
      </div>
    );
  }

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <div className="page-title">{t('Kontakti', 'Contacts')}</div>
          <div className="page-sub">{t('Svi tvoji kontakti za Email, SMS i Viber kampanje na jednom mestu.', 'All your contacts for Email, SMS and Viber campaigns in one place.')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          <Icon name="plus" size={16} /> {t('Dodaj kontakt', 'Add contact')}
        </button>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--ok)', color: 'var(--ok)', marginBottom: 16 }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <div className="flex items-center justify-between wrap gap-16" style={{ marginBottom: 16 }}>
        <div className="search">
          <Icon name="search" size={16} />
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('Pretraži po imenu ili email-u', 'Search by name or email')}
          />
        </div>
        <div className="flex items-center gap-12 wrap">
          {selected.length > 0 && (
            <>
              <span className="chip"><b className="tnum">{selected.length}</b> {t('izabrano', 'selected')}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/contact-lists')}>
                <Icon name="users" size={15} /> {t('Dodaj u listu', 'Add to list')}
              </button>
            </>
          )}
          <label className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', fontSize: 13.5, color: 'var(--text-2)' }}>
            <input
              type="checkbox"
              className="row-check"
              checked={showUnsubscribedOnly}
              onChange={(e) => {
                setShowUnsubscribedOnly(e.target.checked);
                setPage(1);
              }}
            />
            {t('Samo odjavljeni', 'Unsubscribed only')}
          </label>
        </div>
      </div>

      <div className="card">
        {contacts.length === 0 ? (
          <Empty
            icon="contacts"
            title={t('Nema kontakata', 'No contacts')}
            sub={t('Dodaj prvi kontakt da biste počeli.', 'Add your first contact to get started.')}
            action={
              <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                <Icon name="plus" size={16} /> {t('Dodaj kontakt', 'Add contact')}
              </button>
            }
          />
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 44, paddingLeft: 22 }}>
                    <input type="checkbox" className="row-check" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <th>{t('Ime', 'Name')}</th>
                  <th>{t('Telefon', 'Phone')}</th>
                  <th>{t('Oznake', 'Tags')}</th>
                  <th>{t('Status', 'Status')}</th>
                  <th>{t('Kreirano', 'Created')}</th>
                  <th />
                </tr>
              </thead>
              <tbody ref={menuRef}>
                {contacts.map((c) => {
                  const tags = tagsOf(c);
                  return (
                    <tr key={c.id}>
                      <td style={{ paddingLeft: 22 }}>
                        <input
                          type="checkbox"
                          className="row-check"
                          checked={selected.includes(c.id)}
                          onChange={() => toggleOne(c.id)}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-12">
                          <span className="av-mini" style={{ background: avatarColor(c.id) }}>
                            {initials(c.name, c.email)}
                          </span>
                          <div
                            style={{ minWidth: 0, cursor: 'pointer' }}
                            onClick={() => setOverviewId(c.id)}
                            title={t('Pregled kontakta', 'Contact overview')}
                          >
                            <div style={{ fontWeight: 700 }} className="link-hover">{c.name || '—'}</div>
                            <div className="dim" style={{ fontSize: 12.5 }}>{c.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="muted mono" style={{ fontSize: 13 }}>{c.phoneNumber || '—'}</td>
                      <td>
                        <div className="flex gap-8 wrap">
                          {tags.length ? (
                            tags.map((tag, i) => (
                              <span key={i} className="chip" style={{ height: 24, fontSize: 11.5 }}>{tag}</span>
                            ))
                          ) : (
                            <span className="dim">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-8 wrap">
                          {c.unsubscribed ? (
                            <span className="badge badge-failed"><span className="dot" /> {t('Odjavljen', 'Unsubscribed')}</span>
                          ) : (
                            <span className="badge badge-completed"><span className="dot" /> {t('Pretplaćen', 'Subscribed')}</span>
                          )}
                          {c.blacklisted && (
                            <span className="badge badge-failed"><span className="dot" /> {t('Crna lista', 'Blacklist')}</span>
                          )}
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: 13 }}>{fmtDate(c.createdAt)}</td>
                      <td style={{ position: 'relative', textAlign: 'right', paddingRight: 18 }}>
                        <button
                          className="icon-btn"
                          aria-label={t('Akcije', 'Actions')}
                          onClick={(e) => { e.stopPropagation(); setMenu(menu === c.id ? null : c.id); }}
                        >
                          <Icon name="more" size={18} />
                        </button>
                        {menu === c.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', right: 18, top: 44, zIndex: 20, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-lg)', padding: 6, width: 200, textAlign: 'left' }}
                          >
                            <MenuItem icon="eye" label={t('Pregled', 'Overview')} onClick={() => { setMenu(null); setOverviewId(c.id); }} />
                            {c.unsubscribed ? (
                              <MenuItem icon="checkCircle" label={t('Aktiviraj', 'Activate')} onClick={() => { setMenu(null); handleToggleUnsubscribe(c); }} />
                            ) : (
                              <MenuItem icon="x" label={t('Označi kao odjavljen', 'Mark as unsubscribed')} onClick={() => { setMenu(null); handleToggleUnsubscribe(c); }} />
                            )}
                            {c.blacklisted ? (
                              <MenuItem icon="checkCircle" label={t('Skini sa crne liste', 'Remove from blacklist')} onClick={() => { setMenu(null); handleToggleBlacklist(c); }} />
                            ) : (
                              <MenuItem icon="alert" label={t('Dodaj na crnu listu (SMS/Viber)', 'Blacklist (SMS/Viber)')} onClick={() => { setMenu(null); handleToggleBlacklist(c); }} />
                            )}
                            <MenuItem icon="trash" label={t('Obriši', 'Delete')} danger onClick={() => { setMenu(null); handleDeleteContact(c.id); }} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between" style={{ padding: '14px 18px' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <Icon name="chevronLeft" size={16} /> {t('Prethodna', 'Previous')}
                </button>
                <span className="muted" style={{ fontSize: 13 }}>
                  {t('Strana', 'Page')} <b className="tnum">{page}</b> {t('od', 'of')} <b className="tnum">{totalPages}</b>
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  {t('Sledeća', 'Next')} <Icon name="arrowRight" size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="card-title">{t('Dodaj novi kontakt', 'Add new contact')}</div>
              <button className="icon-btn" aria-label={t('Zatvori', 'Close')} onClick={() => setShowAddModal(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="grid-2">
                <div className="field">
                  <label className="label">{t('Ime', 'Name')}</label>
                  <input
                    className="input"
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder={t('Ime i prezime (opciono)', 'Full name (optional)')}
                  />
                </div>
                <div className="field">
                  <label className="label">{t('Telefon', 'Phone')} <span className="dim">(SMS/Viber)</span></label>
                  <input
                    className="input"
                    type="tel"
                    value={newContact.phoneNumber}
                    onChange={(e) => setNewContact({ ...newContact, phoneNumber: e.target.value })}
                    placeholder="npr. +381648382015"
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Email *</label>
                <input
                  className="input"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="primer@email.com"
                  required
                />
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">{t('Dodaj u liste (opciono)', 'Add to lists (optional)')}</label>
                {availableLists.length === 0 ? (
                  <div className="hint">{t('Nema lista. Kreiraj listu u sekciji „Liste".', 'No lists. Create a list in the "Lists" section.')}</div>
                ) : (
                  <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 8 }}>
                    {availableLists.map((list) => (
                      <label key={list.id} className="flex items-center gap-8" style={{ padding: '6px 4px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="row-check"
                          checked={selectedLists.includes(list.id)}
                          onChange={(e) => {
                            setSelectedLists((prev) =>
                              e.target.checked ? [...prev, list.id] : prev.filter((x) => x !== list.id)
                            );
                          }}
                        />
                        <span className="grow">{list.name}</span>
                        {typeof list.contactCount === 'number' && (
                          <span className="dim tnum" style={{ fontSize: 12 }}>{list.contactCount}</span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>{t('Otkaži', 'Cancel')}</button>
              <button className="btn btn-primary" onClick={handleAddContact}>
                <Icon name="plus" size={16} /> {t('Dodaj kontakt', 'Add contact')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Overview Modal */}
      {overviewId != null && (
        <div className="overlay" onClick={() => setOverviewId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="card-title">{t('Pregled kontakta', 'Contact overview')}</div>
              <button className="icon-btn" aria-label={t('Zatvori', 'Close')} onClick={() => setOverviewId(null)}>
                <Icon name="x" size={18} />
              </button>
            </div>

            <div className="modal-body">
              {overviewLoading && !overview ? (
                <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
              ) : !overview ? (
                <div className="muted">{t('Kontakt nije pronađen.', 'Contact not found.')}</div>
              ) : (
                <>
                  <div className="flex items-center gap-12" style={{ marginBottom: 16 }}>
                    <span className="av-mini" style={{ background: avatarColor(overview.id) }}>
                      {initials(overview.name, overview.email)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{overview.name || '—'}</div>
                      <div className="dim" style={{ fontSize: 12.5 }}>{overview.email || '—'}</div>
                      {overview.phoneNumber && <div className="muted mono" style={{ fontSize: 12.5 }}>{overview.phoneNumber}</div>}
                    </div>
                    <div className="flex items-center gap-8 wrap" style={{ marginLeft: 'auto' }}>
                      {overview.unsubscribed && <span className="badge badge-failed"><span className="dot" /> {t('Odjavljen', 'Unsubscribed')}</span>}
                      {overview.blacklisted && <span className="badge badge-failed"><span className="dot" /> {t('Crna lista', 'Blacklist')}</span>}
                    </div>
                  </div>

                  <div className="field" style={{ marginBottom: 16 }}>
                    <label className="label">{t('Liste', 'Lists')} <span className="dim">({(overview.lists || []).length})</span></label>
                    {(overview.lists || []).length === 0 ? (
                      <div className="hint">{t('Kontakt nije ni u jednoj listi.', 'Contact is not in any list.')}</div>
                    ) : (
                      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 8 }}>
                        {(overview.lists || []).map((list) => (
                          <div key={list.id} className="flex items-center gap-8" style={{ padding: '6px 4px' }}>
                            <Icon name="users" size={15} />
                            <span className="grow">{list.name}</span>
                            <button
                              className="btn btn-ghost btn-sm"
                              disabled={overviewBusy}
                              onClick={() => removeContactFromList(list.id)}
                              title={t('Ukloni iz liste', 'Remove from list')}
                            >
                              <Icon name="x" size={14} /> {t('Ukloni', 'Remove')}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {(() => {
                    const currentIds = new Set((overview.lists || []).map((l) => l.id));
                    const addable = availableLists.filter((l) => !currentIds.has(l.id));
                    return (
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label className="label">{t('Dodaj u listu', 'Add to list')}</label>
                        {addable.length === 0 ? (
                          <div className="hint">{t('Kontakt je već u svim dostupnim listama.', 'Contact is already in all available lists.')}</div>
                        ) : (
                          <div className="flex items-center gap-8">
                            <select
                              className="input grow"
                              value={addToListId}
                              onChange={(e) => setAddToListId(e.target.value)}
                              disabled={overviewBusy}
                            >
                              <option value="">{t('— izaberi listu —', '— select a list —')}</option>
                              {addable.map((l) => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                              ))}
                            </select>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={overviewBusy || !addToListId}
                              onClick={() => addToListId && addContactToList(parseInt(addToListId))}
                            >
                              <Icon name="plus" size={15} /> {t('Dodaj', 'Add')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div className="modal-foot">
              {canSeeMessages && overview && (
                <button
                  className="btn btn-secondary"
                  style={{ marginRight: 'auto' }}
                  onClick={() => navigate(`/messages?contactId=${overview.id}`)}
                >
                  <Icon name="inbox" size={16} /> {t('Pregled poruka', 'View messages')}
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => setOverviewId(null)}>{t('Zatvori', 'Close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{ icon: any; label: string; onClick: () => void; danger?: boolean }> = ({ icon, label, onClick, danger }) => (
  <button
    className="lrow"
    style={{ width: '100%', padding: '9px 11px', border: 'none', background: 'transparent', fontWeight: 600, fontSize: 13.5, color: danger ? 'var(--danger)' : 'var(--text)', gap: 10, cursor: 'pointer' }}
    onClick={onClick}
  >
    <Icon name={icon} size={16} /> {label}
  </button>
);

export default Contacts;
